import { Link } from "react-router-dom";
import { ArrowUpRight, Briefcase, Building2, GraduationCap, MapPin, Trophy } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { subjectSlug } from "@/lib/rankingSlug";
import { Bar, Source } from "@/components/app/featured-cities/CityBlocks";
import { label, slugTestId } from "@/components/app/featured-cities/cityMeta";

const tile = "rounded-lg border border-stone-200 bg-white p-5 sm:p-6 min-w-0";
const u = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1600&q=70`;
export const SUBJECT_META = {
  "Computer Science": { photo: u("photo-1555066931-4365d14bab8c"), accent: "#1D4ED8" },
  Business: { photo: u("photo-1517048676732-d65bc937f952"), accent: "#B45309" },
  Engineering: { photo: u("photo-1581091226825-a6a2a5aee158"), accent: "#0F766E" },
  Medicine: { photo: u("photo-1579684453423-f84349ef60b0"), accent: "#BE123C" },
  Law: { photo: u("photo-1667849921481-9e13c239ee3d"), accent: "#7C2D12" },
  Economics: { photo: u("photo-1611974789855-9c2a0a7236a3"), accent: "#047857" },
  Psychology: { photo: u("photo-1617791160536-598cf32026fb"), accent: "#7C3AED" },
  "Data Science": { photo: u("photo-1551288049-bebda4e38f71"), accent: "#0369A1" },
};
export const subjectMeta = (s) => SUBJECT_META[s] || { photo: null, accent: "#002147" };
export const rankingUrl = (s, cc = "uk") => `/rankings/${cc.toLowerCase()}/${subjectSlug(s)}`;

function Head({ icon: Icon, children, color }) {
  return <p className="font-data text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2 mb-4"><Icon className="h-4 w-4" style={{ color }} /> {children}</p>;
}

export function SubjectHero({ s, badge }) {
  const { formatFrom } = useCurrency();
  const m = subjectMeta(s.subject);
  return (
    <div className="relative overflow-hidden rounded-lg min-h-[320px] lg:min-h-[380px] bg-stone-900 text-white lg:col-span-8 min-w-0" data-testid={`featured-subjects-header-${slugTestId(s.subject)}`}>
      {m.photo && <img src={m.photo} alt={s.subject} className="absolute inset-0 h-full w-full object-cover" data-testid="featured-subjects-image" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
      {badge && <div className="absolute top-4 right-4 z-10">{badge}</div>}
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 gap-4">
        <span className="font-data text-xs uppercase tracking-widest px-2 py-1 rounded self-start" style={{ background: m.accent }}>Featured subject</span>
        <h3 className="font-editorial text-4xl sm:text-5xl lg:text-6xl leading-none text-white" data-testid="featured-subjects-name">{s.subject}</h3>
        <p className="text-sm sm:text-base text-stone-200 max-w-2xl" data-testid="featured-subjects-tagline">
          {s.courses.toLocaleString()} courses at {s.universities.toLocaleString()} universities in {s.countries} countries.
        </p>
        {s.median_pay_usd && (
          <div className="flex flex-wrap gap-6 font-data" data-testid="featured-subjects-pay-stat">
            <span><strong className="text-2xl sm:text-3xl">{formatFrom(s.median_pay_usd, "USD")}</strong> <span className="text-xs text-stone-300">typical graduate pay</span></span>
            {s.pay_p90_usd && <span><strong className="text-2xl sm:text-3xl">{formatFrom(s.pay_p90_usd, "USD")}</strong> <span className="text-xs text-stone-300">top 10% of courses</span></span>}
          </div>
        )}
      </div>
    </div>
  );
}

export function SubjectKpis({ s }) {
  const { formatFrom } = useCurrency();
  const k = [["Courses", s.courses.toLocaleString(), "courses"], ["Universities", s.universities.toLocaleString(), "universities"],
    ["Median grad pay", s.median_pay_usd ? formatFrom(s.median_pay_usd, "USD") : "—", "pay"],
    ["In work or study", s.employment_rate ? `${Math.round(s.employment_rate * 100)}%` : "—", "employment"]];
  return (
    <div className="rounded-lg bg-stone-900 text-stone-100 p-6 lg:col-span-4 grid grid-cols-2 gap-5 content-between min-w-0" data-testid="featured-subjects-kpi-tile">
      {k.map(([n, v, id]) => (
        <div key={n} className="min-w-0">
          <p className="font-data text-3xl sm:text-4xl font-semibold tracking-tight break-words" data-testid={`featured-subjects-kpi-${id}`}>{v}</p>
          <p className="font-data text-[11px] uppercase tracking-widest text-stone-400 mt-1">{n}</p>
        </div>
      ))}
      <p className="col-span-2 font-data text-[11px] text-stone-500 italic">Source: WUA course database · pay from {s.pay_sample.toLocaleString()} courses, employment from {s.employment_sample.toLocaleString()}</p>
    </div>
  );
}

export function RankingTile({ s, accent }) {
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-subjects-ranking-block">
      <Head icon={Trophy} color={accent}>WUA subject ranking</Head>
      {s.rankings.map((r) => (
        <div key={r.country} className="mb-4" data-testid={`featured-subjects-ranking-${r.country.toLowerCase()}`}>
          <p className="text-sm text-stone-600 mb-2">{r.country === "UK" ? "United Kingdom" : "United States"} · top of {r.qualified}</p>
          <ol className="space-y-2">
            {r.rows.slice(0, r.country === "UK" ? 5 : 3).map((x) => (
              <li key={x.id} className="flex items-center gap-2 text-sm">
                <span className="font-data text-xs w-5 text-stone-500">#{x.rank}</span>
                <Link to={`/uni/${x.id}`} className="flex-1 min-w-0 truncate text-stone-900 hover:underline" data-testid={`featured-subjects-rank-uni-${x.id}`}>{x.name}</Link>
                <span className="font-data text-xs" style={{ color: accent }}>{x.score}</span>
              </li>
            ))}
          </ol>
          <Link to={rankingUrl(s.subject, r.country)} className="inline-flex items-center gap-1 text-xs font-medium mt-2 hover:underline" style={{ color: accent }} data-testid={`featured-subjects-ranking-link-${r.country.toLowerCase()}`}>
            Full {r.country} ranking <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ))}
      <Source>WUA ranking: graduate pay, value, admissibility, satisfaction, completion</Source>
    </div>
  );
}

export function PayByCountry({ s, accent }) {
  const { formatFrom } = useCurrency();
  const rows = s.by_country.filter((c) => c.median_pay_usd);
  const max = Math.max(...rows.map((c) => c.median_pay_usd), 1);
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-subjects-pay-block">
      <Head icon={Briefcase} color={accent}>Graduate pay by country</Head>
      <div className="space-y-3 mb-4">
        {rows.map((c) => (
          <div key={c.code} data-testid={`featured-subjects-country-${c.code}`}>
            <p className="flex justify-between text-sm mb-1"><span className="text-stone-800">{c.name} <span className="text-xs text-stone-500">· {c.courses.toLocaleString()} courses</span></span><span className="font-data text-xs">{formatFrom(c.median_pay_usd, "USD")}</span></p>
            <Bar value={c.median_pay_usd} max={max} color={accent} />
          </div>
        ))}
      </div>
      {s.entry.common.length > 0 && (
        <div className="rounded px-3 py-2 bg-stone-50 border border-stone-200 mb-3" data-testid="featured-subjects-entry">
          <p className="font-data text-[11px] uppercase tracking-widest text-stone-500">Typical A-level offers</p>
          <p className="font-data text-lg font-semibold text-stone-900">{s.entry.common.join(" · ")}</p>
          <p className="text-[11px] text-stone-500">most common across {s.entry.sample} UK courses</p>
        </div>
      )}
      <Source>Median of course-level graduate salaries (LEO, College Scorecard, national surveys)</Source>
    </div>
  );
}

export function CitiesTile({ s, accent }) {
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-subjects-cities-block">
      <Head icon={MapPin} color={accent}>Where to study it</Head>
      <ul className="space-y-2 mb-5">
        {s.top_cities.map((c) => (
          <li key={c.name} className="flex items-center justify-between text-sm" data-testid={`featured-subjects-city-${slugTestId(c.name)}`}>
            {c.slug ? <Link to={`/cities/${c.slug}`} className="text-stone-900 hover:underline">{c.name}</Link> : <span className="text-stone-900">{c.name}</span>}
            <span className="font-data text-xs text-stone-500">{c.courses} courses</span>
          </li>
        ))}
      </ul>
      {s.industry.map((h) => (
        <div key={h.tag} className="mb-3" data-testid={`featured-subjects-hub-${h.tag}`}>
          <p className="text-sm text-stone-600 mb-1">Top cities for {label(h.tag)} careers <span className="text-xs">(of {h.of})</span></p>
          <div className="flex flex-wrap gap-1.5">
            {h.cities.map((c) => (
              <Link key={c.slug} to={`/cities/${c.slug}`} className="text-xs px-2 py-1 rounded-full border hover:bg-stone-50" style={{ borderColor: accent, color: accent }}>#{c.rank} {c.name}</Link>
            ))}
          </div>
        </div>
      ))}
      <Source>WUA course database · city career index</Source>
    </div>
  );
}

export function CoursesTile({ s, accent }) {
  const { formatFrom } = useCurrency();
  return (
    <div className="rounded-lg border border-stone-200 bg-[#FBF8F3] p-5 sm:p-6 lg:col-span-8 grid gap-6 md:grid-cols-2 min-w-0" data-testid="featured-subjects-courses-block">
      <div className="min-w-0">
        <Head icon={GraduationCap} color={accent}>Highest-paid undergraduate courses</Head>
        <ul className="space-y-3">
          {s.top_courses.map((c) => (
            <li key={c.id}>
              <Link to={`/degree/${c.id}`} className="group block" data-testid={`featured-subjects-course-${c.id}`}>
                <span className="font-data text-lg font-semibold text-stone-900">{formatFrom(c.median_pay_usd, "USD")}</span>
                <span className="block text-sm text-stone-800 group-hover:underline break-words">{c.title}</span>
                <span className="block text-xs text-stone-500">{c.university}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      {s.employers.length > 0 && (
        <div className="min-w-0">
          <Head icon={Building2} color={accent}>Where graduates go</Head>
          <div className="flex flex-wrap gap-1.5">
            {s.employers.map((e) => (
              <span key={e} className="text-xs px-2 py-1 rounded-full border bg-white" style={{ borderColor: accent, color: accent }} data-testid={`featured-subjects-employer-${slugTestId(e)}`}>{e}</span>
            ))}
          </div>
          <p className="font-data text-[11px] text-stone-400 italic mt-3">Source: employer destinations on course pages (partly editorial by subject)</p>
        </div>
      )}
    </div>
  );
}

export function SubjectCta({ s, share }) {
  return (
    <div className="rounded-lg bg-[#002147] text-white p-6 lg:col-span-4 flex flex-col justify-between gap-6 min-w-0" data-testid="featured-subjects-cta-tile">
      <p className="font-editorial text-2xl sm:text-3xl leading-tight">Where does {s.subject} take you?</p>
      <div className="flex flex-col gap-2">
        <Link to={rankingUrl(s.subject, s.rankings[0]?.country || "uk")} className="inline-flex items-center justify-between rounded-full bg-white text-[#002147] px-5 py-3 font-medium transition-transform hover:-translate-y-0.5" data-testid="featured-subjects-ranking-button">
          See the full {s.subject} ranking <ArrowUpRight className="h-4 w-4" />
        </Link>
        {share}
      </div>
    </div>
  );
}
